import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { UrlHelper } from '@/utils/UrlHelper';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { useSuperAdminOrdersFilter } from '@/features/superAdmin/features/data/orders/hooks/useSuperAdminOrdersFilter';
import { SuperAdminOrderListResponseDto } from '@/features/superAdmin/features/data/types/superAdmin-data.types';

type Props = {
    suspendFetching?: boolean;
};

export const useSuperAdminOrdersListApi = ({ suspendFetching }: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const { filter } = useSuperAdminOrdersFilter();
    const {
        take,
        skip,
        startDate,
        endDate,
        tenantId,
        ownerId,
        apartmentId,
        orderBy,
        orderDirection,
    } = filter;

    const { data, isLoading, error } = useQuery({
        queryKey: [
            ApiConfig.queries.superAdmin.data.orders.queryKeys.list,
            take,
            skip,
            startDate,
            endDate,
            tenantId,
            ownerId,
            apartmentId,
            orderBy,
            orderDirection,
        ],
        queryFn: async (): Promise<SuperAdminOrderListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(
                ApiConfig.queries.superAdmin.data.orders.endpoints.list,
                {
                    skip,
                    take,
                    startDate: DateTimeHelper.getDateOnly(startDate),
                    endDate: DateTimeHelper.getDateOnly(endDate),
                    tenantId: tenantId ?? undefined,
                    ownerId: ownerId ?? undefined,
                    apartmentId: apartmentId ?? undefined,
                    orderBy: orderBy ?? undefined,
                    orderDirection,
                },
            );
            const response = await axiosInstance.get<SuperAdminOrderListResponseDto>(url);
            return response.data;
        },
        enabled: !suspendFetching,
        placeholderData: keepPreviousData,
    });

    return {
        list: data,
        isLoading,
        error,
    };
};
